/**
 * The campaign's cost estimate (platform design §6.6, §5.4, frame 132:555):
 * `GET /campaigns/{c}/burn-rate` (`useCampaignBurnRate`) summed per media
 * kind across every tracked account, never as one scalar.
 *
 * `rate_per_day === null` is not a pricing failure: it is `estimateSchema`'s
 * structural marker for an ephemeral kind (STORY), which is priced through
 * `story_credits_per_day` instead of a rate (§5.4). The estimator never
 * emits a lifecycle kind (POST, REEL) with no rate — it falls back to a
 * recorded default rather than leaving one unset (§6.6) — so every kind's
 * `credits_per_day` is always a real, priced number and the totals below
 * never fold to an absence.
 *
 * Fail-closed for a caller without `control_plane.write`, same idiom as
 * `TenantHome`/`Accounts`/`Catalogue` (§17.15), until
 * `owner-decisions-fs-campaign-record-and-accounts-analyst-credit-figures`
 * rules on Analista visibility (§5.1).
 */

import { useState, type ReactNode } from 'react';

import { getLocale } from '../../../i18n';
import { compact, credits } from '../../../i18n/format';
import { Absent } from '../../../chrome/measure';
import { Page, PageHeader, PageSection, StatCard, StatGrid } from '../../../chrome/page';
import { EmptyState, LoadingState, QueryStates } from '../../../chrome/state';
import { useCan } from '../../session/orgContext';
import { MEDIA_KINDS, type BurnRate, type Estimate } from '../../control/api/schemas';
import { useCampaignBurnRate } from '../accounts/api/queries';
import { CommentLimit } from './CommentLimit';
import { CreditDestination } from './CreditDestination';
import { et } from './messages';

type MediaKind = (typeof MEDIA_KINDS)[number];
type KindNode = NonNullable<Estimate['kinds'][MediaKind]>;

interface KindRow {
  kind: MediaKind;
  /** `null` while an operation the kind runs has no rate. */
  creditsPerDay: number | null;
}

function kindRows(data: BurnRate): KindRow[] {
  return MEDIA_KINDS.flatMap((kind) => {
    const entries: KindNode[] = [];
    for (const account of data.per_account) {
      const entry = account.estimate.kinds[kind];
      if (entry !== undefined) entries.push(entry);
    }
    if (entries.length === 0) return [];
    const figures = entries.map((entry) => entry.credits_per_day);
    const creditsPerDay = figures.every((figure) => figure !== null)
      ? figures.reduce((sum, figure) => sum + figure, 0)
      : null;
    return [{ kind, creditsPerDay }];
  });
}

function nothingEnabled(data: BurnRate): boolean {
  return data.per_account.every((account) => Object.keys(account.estimate.kinds).length === 0);
}

interface CommentCapExposure {
  /** The slice of the campaign's daily total this what-if can move: every
   *  priced kind's `comments_credits_per_day` for which the same account
   *  also carries a tail scenario (§6.6 statement 1) to read a cap
   *  sensitivity from. */
  commentsPerDay: number;
  /** How much steeper that same slice reads once the per-post cap no
   *  longer binds — `tail`'s own uncapped ÷ capped ratio, aggregated
   *  credit-weighted across every account and kind rather than per kind,
   *  since the slider moves the campaign's two totals, not one kind's row. */
  uplift: number;
}

/** `null` when nothing here has a comment cap that binds: a kind's tail
 *  scenario prices `capped_credits_per_item` equal to `uncapped_credits_per_item`
 *  whenever the configured cap sits above what the scenario's comment
 *  volume would ever reach (§6.6), and there is nothing to explore. */
function commentCapExposure(data: BurnRate): CommentCapExposure | null {
  let commentsPerDay = 0;
  let cappedPerDay = 0;
  let uncappedPerDay = 0;
  for (const account of data.per_account) {
    for (const kind of MEDIA_KINDS) {
      const tail = account.estimate.tail[kind];
      const node = account.estimate.kinds[kind];
      if (tail === undefined || node === undefined || node.rate_per_day === null) continue;
      // A comment line with no rate has no figure to move: the what-if is not offered.
      if (node.comments_credits_per_day === null) return null;
      commentsPerDay += node.comments_credits_per_day;
      cappedPerDay += node.rate_per_day * tail.capped_credits_per_item;
      uncappedPerDay += node.rate_per_day * tail.uncapped_credits_per_item;
    }
  }
  if (cappedPerDay <= 0 || uncappedPerDay <= cappedPerDay) return null;
  return { commentsPerDay, uplift: uncappedPerDay / cappedPerDay };
}

export function CostEstimate(props: { campaignId: string }): ReactNode {
  const canView = useCan('control_plane.write');
  const query = useCampaignBurnRate(props.campaignId);
  const locale = getLocale();
  // Percent of the comment-cap what-if's own effect (0 = as capped today,
  // 100 = as if the cap were removed). Local only — see `CommentLimit`.
  const [liftPercent, setLiftPercent] = useState(0);

  if (!canView) return null;

  return (
    <Page>
      <PageHeader title={et('title')} description={et('lead')} />
      <QueryStates
        query={query}
        isEmpty={nothingEnabled}
        empty={<EmptyState title={et('empty.title')} description={et('empty.body')} />}
        loading={<LoadingState lines={4} />}
      >
        {(data) => {
          const rows = kindRows(data);
          const priced =
            data.totals.estimated_credits_per_day !== null &&
            data.totals.estimated_credits_per_30d !== null;
          const exposure = priced ? commentCapExposure(data) : null;
          // No ternary defaulting to a literal 0: with no exposure to explore
          // there is nothing to add, so the totals below are the server's own
          // rather than the server's plus a zero this what-if invented.
          const liftFraction = liftPercent / 100;
          const commentsShift =
            exposure === null
              ? undefined
              : exposure.commentsPerDay * liftFraction * (exposure.uplift - 1);
          const dayBase = data.totals.estimated_credits_per_day;
          const monthBase = data.totals.estimated_credits_per_30d;
          const dayTotal =
            dayBase === null ? null : dayBase + (commentsShift === undefined ? 0 : commentsShift);
          const monthTotal =
            monthBase === null
              ? null
              : monthBase + (commentsShift === undefined ? 0 : commentsShift * 30);

          return (
            <div className="flex flex-col gap-6">
              <StatGrid>
                <StatCard
                  label={et('stat.day')}
                  value={
                    <span data-testid="stat-day">
                      {dayTotal === null ? et('unpriced') : compact(dayTotal, locale)}
                    </span>
                  }
                />
                <StatCard
                  label={et('stat.30d')}
                  value={
                    <span data-testid="stat-30d">
                      {monthTotal === null ? et('unpriced') : compact(monthTotal, locale)}
                    </span>
                  }
                />
                <StatCard
                  label={et('stat.actual7d')}
                  value={
                    <span data-testid="stat-actual-7d">
                      {data.actuals.trailing_7d_credits === null ? (
                        <Absent reason={{ text: et('stat.actualNone') }} />
                      ) : (
                        credits(data.actuals.trailing_7d_credits, locale)
                      )}
                    </span>
                  }
                />
              </StatGrid>

              <PageSection title={et('breakdown.title')} description={et('breakdown.lead')}>
                <StatGrid>
                  {rows.map((row) => (
                    <StatCard
                      key={row.kind}
                      label={et(`kind.${row.kind}`)}
                      value={
                        <span data-testid={`kind-${row.kind}`}>
                          {row.creditsPerDay === null
                            ? et('unpriced')
                            : compact(row.creditsPerDay, locale)}
                        </span>
                      }
                    />
                  ))}
                </StatGrid>
              </PageSection>

              {exposure !== null && (
                <PageSection title={et('commentLimit.title')} description={et('commentLimit.lead')}>
                  <CommentLimit value={liftPercent} onChange={setLiftPercent} />
                </PageSection>
              )}

              <PageSection title={et('credits.title')}>
                <CreditDestination rows={data.per_account} />
              </PageSection>
            </div>
          );
        }}
      </QueryStates>
    </Page>
  );
}
